import { makeAutoObservable } from "mobx";
import { isBadRequestError } from "@/api";
import { companyApiClient } from "@/api/companyApiClient";
import { failure, ok, pending, Result } from "@/common/common";
import { NumberFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { ShareType, ShareTypeCode, shareTypeLabel } from "@/common/share";
import {
  DCEvent,
  HttpEventCallbacks,
  navigate,
  showError,
  showGeneralError,
  thunk,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { adminShareholdersShareClasses } from "@/utils/navigation";
import { shareTypeApiClient } from "./shareTypeApiClient";

export class ShareClassModel {
  orgNo: string;
  shareClass: RadioFieldModel<ShareTypeCode | typeof CustomShareClass | null>;
  reportingShareClass: RadioFieldModel<ShareTypeCode>;
  numberOfVotes: NumberFieldModel;
  useCustomName: boolean;
  shareTypeNames: ShareTypeNameModel[];

  constructor({
    orgNo,
    availableShareTypes,
    existingShareTypes,
    editValue,
  }: {
    orgNo: string;
    availableShareTypes: ShareTypeCode[];
    existingShareTypes: ShareType[];
    editValue?: ShareType;
  }) {
    this.orgNo = orgNo;
    const shareTypeCodes = filterUsedSharetypeCodes(availableShareTypes, existingShareTypes);
    this.shareClass = new RadioFieldModel<ShareTypeCode | typeof CustomShareClass | null>({
      value: editValue ? editValue.shareTypeCode : null,
      options: [...shareTypeCodes, CustomShareClass],
      idFn: (s) => String(s),
      labelFn: (option) =>
        option
          ? option === CustomShareClass
            ? { key: "shareType.custom-shareclass" }
            : shareTypeLabel(option)
          : { key: "shareClasses.select-shareClass" },
      required: true,
      iconFn: (option) => (option === CustomShareClass ? "gear" : undefined),
    });
    this.reportingShareClass = new RadioFieldModel<ShareTypeCode>({
      value: editValue?.shareTypeCode ?? shareTypeCodes[0],
      options: shareTypeCodes,
      idFn: (s) => String(s),
      labelFn: (option) => shareTypeLabel(option),
      required: true,
    });
    this.numberOfVotes = new NumberFieldModel({
      value: editValue?.vote ?? 1,
      isPositiveOrZero: true,
      required: true,
    });
    this.useCustomName = editValue?.shareTypeNames !== undefined;
    this.shareTypeNames = editValue?.shareTypeNames
      ? editValue.shareTypeNames.map(
          (name) => new ShareTypeNameModel(name.language, name.name, name.separator),
        )
      : Object.values(Language).map((language) => new ShareTypeNameModel(language));
    makeAutoObservable(this);
  }

  validate() {
    if (this.hasCustomName) {
      this.shareTypeNames.forEach((model) => model.name.validate());
    }
    return (
      this.shareClass.validate() &&
      this.numberOfVotes.validate() &&
      (!this.hasCustomName || this.shareTypeNames.every((model) => model.name.isValid))
    );
  }

  prepareData(): ShareTypeRequest {
    return {
      organizationnumber: this.orgNo,
      vote: this.numberOfVotes.parsedValue!,
      shareTypeCode:
        this.shareClass.value === CustomShareClass
          ? this.reportingShareClass.value
          : this.shareClass.value!,
      shareTypeNames: this.hasCustomName
        ? this.shareTypeNames.map((model) => ({
            language: model.language,
            name: model.name.validatedValue,
            separator: toString(model.separator.value),
          }))
        : undefined,
    };
  }

  setUseCustomName(value: boolean) {
    this.useCustomName = value;
  }

  get hasCustomName() {
    return this.useCustomName || this.shareClass.value === CustomShareClass;
  }
}

export class ShareTypeNameModel {
  language: Language;
  name: StringFieldModel;
  separator: RadioFieldModel<Separator>;

  constructor(language: Language, name?: string, separator?: string) {
    this.language = language;
    this.name = new StringFieldModel({ value: name, required: true });
    this.separator = new RadioFieldModel({
      value: separator !== undefined ? toSeparator(separator) : Separator.SPACE,
      idFn: String,
      labelFn: (value) => separatorLabel[value],
      options: Object.values(Separator),
      required: true,
    });
  }
}

export function toSeparator(s: string): Separator {
  switch (s) {
    case " ":
      return Separator.SPACE;
    case "-":
      return Separator.DASH;
    case "":
      return Separator.NONE;
    default:
      throw new Error("Unknown separator");
  }
}

export function toString(separator: Separator) {
  switch (separator) {
    case Separator.SPACE:
      return " ";
    case Separator.DASH:
      return "-";
    case Separator.NONE:
      return "";
    default:
      throw new Error("Unknown separator");
  }
}

enum Separator {
  SPACE = "SPACE",
  DASH = "DASH",
  NONE = "NONE",
}

export const separatorLabel: { [key in Separator]: TMessage } = {
  [Separator.SPACE]: { key: "shareClasses.separator.space" },
  [Separator.DASH]: { key: "shareClasses.separator.dash" },
  [Separator.NONE]: { key: "shareClasses.separator.none" },
};

export interface ShareTypeRequest {
  organizationnumber: string;
  vote: number;
  shareTypeCode: ShareTypeCode;
  shareTypeNames?: { language: string; name: string; separator: string }[];
}

export function filterUsedSharetypeCodes(
  allShareTypes: ShareTypeCode[],
  existingShareTypes: ShareType[],
): ShareTypeCode[] {
  return allShareTypes
    .filter((shareTypeCode) => shareTypeCode !== ShareTypeCode.UNKNOWN)
    .filter((shareTypeCode) => !shareTypeCode.toString().includes("_"))
    .filter((s) => !existingShareTypes.some((shareType) => shareType.shareTypeCode === s));
}

export const CustomShareClass = "CustomShareClass";

export type ShareTypesState = {
  shareTypeToDelete?: ShareType;
  deleteResult?: Result<TMessage, TMessage>;
  remarkDialog?: RemarkDialogState;
};

export type RemarkDialogState = {
  remarkText: StringFieldModel;
};

const updateShareTypes = (data: Partial<ShareTypesState>): UpdateStoreEvent => ({
  type: "update-store",
  data: { shareTypes: (shareTypes = {}) => ({ ...shareTypes, ...data }) },
});

export const submitShareType = (request: ShareTypeRequest, shareTypeId?: number): DCEvent[] => {
  const params: HttpEventCallbacks<void> = {
    onSuccess: () => [navigate(adminShareholdersShareClasses(request.organizationnumber))],
    onFailure: (err) => [
      shareTypeId === undefined && isBadRequestError(err)
        ? showError({ key: "shareClasses.error.shareTypeCodeUsed" })
        : showGeneralError(),
    ],
  };
  return [
    shareTypeId === undefined
      ? shareTypeApiClient.createShareType(request, params)
      : shareTypeApiClient.editShareType(request, shareTypeId, params),
  ];
};

export const confirmDeleteShareType = (shareType: ShareType): DCEvent[] => [
  updateShareTypes({ shareTypeToDelete: shareType, deleteResult: undefined }),
];

export const cancelDeleteShareType = (): DCEvent[] => [
  updateShareTypes({ shareTypeToDelete: undefined }),
];

export const deleteShareType = (shareTypeId: number, orgNo: string): DCEvent[] => [
  updateShareTypes({ deleteResult: pending() }),
  shareTypeApiClient.deleteShareType(shareTypeId, orgNo, {
    onSuccess: () => [
      updateShareTypes({ deleteResult: ok({ key: "ok" }), shareTypeToDelete: undefined }),
      reloadShareholders(),
    ],
    onFailure: () => [updateShareTypes({ deleteResult: failure({ key: "deleting_failed" }) })],
  }),
];

const reloadShareholders = (): DCEvent => ({
  type: "with-store",
  events: (store) => [thunk(() => store.shareholdersStore.loadAllShareholders())],
});

export const openRemarksDialog = (remark: string | undefined): DCEvent[] => [
  updateShareTypes({ remarkDialog: { remarkText: new StringFieldModel({ value: remark }) } }),
];

export const cancelRemarksDialog = (): DCEvent[] => [updateShareTypes({ remarkDialog: undefined })];

export const submitRemarksDialog = (orgNo: string, remarkDialog: RemarkDialogState): DCEvent[] => [
  companyApiClient.updateRemark(orgNo, remarkDialog.remarkText.inputValue, {
    onSuccess: () => [
      updateShareTypes({ remarkDialog: undefined }),
      { type: "update-company-info" },
    ],
    onFailure: () => [showGeneralError()],
  }),
];
